import * as hooks from '@wordpress/hooks';

const sha1 = require('js-sha1');
const {
  data,
  blocks: { createBlock },
} = wp;

/* Section, Row, Column and DIV Blocks : Background type options */
export const devices = [
  {
    type: 'desktop',
    breakPoint: 9999,
    label: 'Desktop (default)',
  },
  {
    type: 'tab',
    breakPoint: 1200,
    label: 'Tab (≤1200px)',
  },
  {
    type: 'mobile',
    breakPoint: 576,
    label: 'Mobile (≤576px)',
  },
];

/* Slider and Tab slider responsive breakpoints */
export const sliderMediaBreakPoints = [
  {
    type: 'sm',
    breakPoint: 576,
    slug: 'mobile',
    label: 'Mobile',
  },
  {
    type: 'lg',
    breakPoint: 992,
    slug: 'tablet-portrait',
    label: 'Tablet Portrait',
  },
  {
    type: 'xl',
    breakPoint: 1200,
    slug: 'tablet-landscape',
    label: 'Tablet Landscape',
  },
  {
    type: 'xxl',
    breakPoint: 9999,
    slug: 'desktop',
    label: 'Desktop',
  },
];

/* Accordion responsive breakpoints */
export const floatPanelDefaultBreakPoints = [
  {
    label: 'Desktop',
    value: 9999,
  },
  {
    label: 'IPad Landscape',
    value: 1200,
  },
  {
    label: 'IPad',
    value: 992,
  },
  {
    label: 'Mobile',
    value: 576,
  },
];

/**
 * Add default attribute values to devices breakpoints
 * Used in "src/components/background-types/attributes.js"
 *
 * @param {Object} paramOptions Attribute values
 * @return {Object} Returns the attribute values
 */
export const displayDevicesOptions = (paramOptions) => {
  const settings = {};
  for (const device of devices) {
    settings[device.type] = {
      breakpoint: device.breakPoint,
      settings: paramOptions,
    };
  }

  return settings;
};

export const feWp = window.feWp || { hooks: hooks.createHooks() };

/**
 * Set unique identifier to the block
 *
 * @param {string}   name          Block title
 * @param {string}   attributeName Block attribute name
 * @param {Object}   attributes    Block attributes
 * @param {Function} setAttributes Set values for the attributes
 * @param {string}   prefix        Prefix for the attribute
 * @param {string}   suffix        Postfix for the attribute
 * @param {Array}    excludeAttrs  Array of attributes to be excluded
 * @return {void} Return nothing
 */
export const setUniqueIdAttribute = (
  name,
  attributeName,
  attributes,
  setAttributes,
  prefix = '',
  suffix = '',
  excludeAttrs = [],
) => {
  let currentAttributeValue = attributes[attributeName].toString();
  if (typeof currentAttributeValue === 'undefined') return 0;

  if (typeof prefix !== 'undefined') {
    currentAttributeValue = currentAttributeValue.replace(prefix, '');
  }

  if (typeof suffix !== 'undefined') {
    currentAttributeValue = currentAttributeValue.replace(suffix, '');
  }

  // Removing the unique id (Ex: sectionBlockId) and adding the blockName attribute to attributes object
  const newAttributesObject = {
    ...attributes,
    blockName: name,
  };
  delete newAttributesObject[attributeName];

  if (Array.isArray(excludeAttrs)) {
    excludeAttrs.forEach((attrName) => {
      if (newAttributesObject[attrName]) {
        delete newAttributesObject[attrName];
      }
    });
  }

  // Converting the attributes object into a string
  const attributesString = JSON.stringify(newAttributesObject);

  // Generating a new hashed id using all the attribute values
  const hashHex = sha1.hex(attributesString);

  // Updating the id if changed
  if (
    currentAttributeValue === null ||
    currentAttributeValue === '0' ||
    currentAttributeValue !== hashHex
  ) {
    setAttributes({
      [attributeName]: `${prefix}${hashHex}${suffix}`,
    });
  }
};

/**
 * Get the column sizes CSS dropdown class related to default CSS library
 *
 * @param {string} defaultCssLibrary Default CSS library
 * @param {Object} columnSize        Column blocks "columnSize" attribute
 * @return {Object} Returns column sizes CSS dropdown classes related to the default CSS library
 */
export const columnSizeClassOutput = (defaultCssLibrary, columnSize) => {
  let columnSizeClass = '';

  switch (defaultCssLibrary) {
    case 'bootstrap':
      columnSizeClass = Object.entries(columnSize)
        // eslint-disable-next-line no-unused-vars
        .filter(([key, value]) => value !== 0)
        .reduce(
          (classes, [key, value]) => `${classes} col-${key}-${value}`,
          '',
        );
      break;

    case 'tailwind':
      columnSizeClass = Object.entries(columnSize)
        // eslint-disable-next-line no-unused-vars
        .filter(([key, value]) => value !== 0)
        .reduce(
          (classes, [key, value]) => `${classes} ${key}:basic-${value}/12`,
          '',
        );
      break;

    default:
      break;
  }

  return columnSizeClass;
};

// Wraps the selected block with the Mobile Content block
export const wrapWithMobileContentBlock = (sourceClientId) => {
  // Getting the source block data from the client id
  const { name, attributes, innerBlocks } = data
    .select('core/block-editor')
    .getBlock(sourceClientId);
  // Creating the target block by adding the source block data as an inner blocks
  const targetBlock = createBlock(
    'b3rg/mobile-content',
    { enableMobileTab: true },
    [
      createBlock('b3rg/desktop-content-wrapper', {}, [
        createBlock(name, attributes, innerBlocks),
      ]),
      createBlock('b3rg/mobile-content-wrapper', {}, [
        createBlock(name, attributes, innerBlocks),
      ]),
    ],
  );
  // Finally, replacing the source block with the target block
  data.dispatch('core/block-editor').replaceBlocks(sourceClientId, targetBlock);
};

// Wraps the selected block with the Mobile Content block
export const wrapWithImageContainer = (sourceClientId) => {
  // Getting the source block data from the client id
  const { name, attributes, innerBlocks } = data
    .select('core/block-editor')
    .getBlock(sourceClientId);
  // Creating the target block by adding the source block data as an inner blocks
  const targetBlock = createBlock(
    'b3rg/image-container',
    { activeTab: 'mobile' },
    [
      createBlock('b3rg/desktop-image', {}, [
        createBlock(name, attributes, innerBlocks),
      ]),
      createBlock('b3rg/mobile-image', {}, [
        createBlock(name, attributes, innerBlocks),
      ]),
    ],
  );
  // Finally, replacing the source block with the target block
  data.dispatch('core/block-editor').replaceBlocks(sourceClientId, targetBlock);
};

// Filter allowed blocks in block
export const filterAllowedBlocks = (allowedBlocksList) => {
  const filteredBlocks = Object.keys(
    allowedBlocksList ? allowedBlocksList : {},
  );
  return filteredBlocks.length > 0 ? allowedBlocksList : null;
};

// Extract all flex classes
export const flexClasses = (flexSettings) => {
  return Object.values(flexSettings)
    .flatMap((arr) => arr.map((item) => item.value))
    .filter(Boolean)
    .join(' ');
};

export const getBlockLabelByName = (attributes, blockName) => {
  return attributes.label || blockName;
};

export const getInnerBlockTemplate = (innerBlock) => {
  const relatedPostTemplate = [];
  const { attributes } = innerBlock;
  if (innerBlock.innerBlocks.length) {
    // Loop through the current inner block's inner blocks
    innerBlock.innerBlocks.forEach((nestedInnerBlock) => {
      let innerBlockTemplate = [];
      if (nestedInnerBlock.innerBlocks.length) {
        // Recursively get the template for the nested inner block
        innerBlockTemplate = [
          nestedInnerBlock.name,
          { ...nestedInnerBlock.attributes },
          getInnerBlockTemplate(nestedInnerBlock),
        ];
      } else {
        innerBlockTemplate = [
          nestedInnerBlock.name,
          { ...nestedInnerBlock.attributes },
        ];
      }
      relatedPostTemplate.push(innerBlockTemplate);
    });
  } else {
    // If no inner blocks, return just the block name and attributes
    return [innerBlock.name, { ...attributes }];
  }
  return relatedPostTemplate;
};

export const cloneChildBlockByParentClientId = (
  clientId,
  firstElmClone = false,
) => {
  const innerBlocks = wp.data
    .select('core/block-editor')
    .getBlockOrder(clientId);

  if (innerBlocks.length === 0) return;
  const blockIndex = firstElmClone ? 0 : innerBlocks.length - 1;
  wp.data
    .dispatch('core/block-editor')
    .duplicateBlocks([innerBlocks[blockIndex]]);
};
